export const PRICING_DEFAULTS = {
  neoMinPerSite: 699, neoStaffRate: 35, mcStandalonePerSite: 999, mcWithNeoPerSite: 799,
  linkBasePerSite: 499, linkIncludedOrders: 1000, linkOveragePerOrder: 0.5,
  bundlePrice: 1699, bundleEnabled: true, billingMode: "eligible_sites",
  enterpriseEnabled: false, enterpriseFlatFee: 10000,
};

export function normalizeBillingRules(raw = {}) {
  const b = raw.billingRules || raw.rules || raw || {}, neo = b.neo || {}, mc = b.morpheus || b.mc || {};
  const link = b.link || {}, bundle = b.bundle || b.matrixosBundle || {}, enterprise = b.enterprise || b.enterpriseBundle || {};
  return {
    neoMinPerSite: Number(neo.minimumPerSite ?? neo.minPerSite ?? b.neoMinPerSite ?? b.neoMinimumPerSite ?? 699),
    neoStaffRate: Number(neo.staffRate ?? b.neoStaffRate ?? 35),
    mcStandalonePerSite: Number(mc.standalonePerSite ?? b.mcStandalonePerSite ?? 999),
    mcWithNeoPerSite: Number(mc.withNeoPerSite ?? mc.neoDiscountPerSite ?? b.mcWithNeoPerSite ?? 799),
    linkBasePerSite: Number(link.basePerSite ?? b.linkBasePerSite ?? 499),
    linkIncludedOrders: Number(link.includedOrders ?? b.linkIncludedOrders ?? 1000),
    linkOveragePerOrder: Number(link.overagePerOrder ?? b.linkOveragePerOrder ?? 0.5),
    bundlePrice: Number(bundle.price ?? bundle.monthlyPrice ?? b.bundlePrice ?? 1699),
    bundleEnabled: bundle.enabled ?? b.bundleEnabled ?? true,
    billingMode: b.strategy?.billingMode || b.billingMode || "eligible_sites",
    enterpriseEnabled: enterprise.enabled ?? b.enterpriseEnabled ?? false,
    enterpriseFlatFee: Number(enterprise.flatFee ?? enterprise.price ?? b.enterpriseFlatFee ?? 10000),
  };
}

const customEnabled = (b) => b.billingRulesOverrideEnabled === true || b.useCustomBillingRules === true || b.pricingOverride === true;
const rateKeys = ["neoMinPerSite", "neoStaffRate", "mcStandalonePerSite", "mcWithNeoPerSite", "linkBasePerSite", "linkIncludedOrders", "linkOveragePerOrder", "bundlePrice"];
export function initialPricingForm(b = {}, defaults = PRICING_DEFAULTS, preview, platform = defaults) {
  const custom = b.billingRules || b.rules || {}, neo = custom.neo || {}, mc = custom.morpheus || custom.mc || {};
  const link = custom.link || {}, bundle = custom.bundle || custom.matrixosBundle || {};
  const global = platform.billingRules || platform.rules || platform;
  const effectiveNeo = { ...(global.neo || {}), ...neo }, effectiveMc = { ...(global.morpheus || global.mc || {}), ...mc };
  // Match the existing server's nested-rule precedence, including legacy flat fields.
  const rates = customEnabled(b) ? {
    neoMinPerSite: effectiveNeo.minimumPerSite ?? effectiveNeo.minPerSite ?? custom.neoMinimumPerSite ?? global.neoMinimumPerSite ?? custom.neoMinPerSite ?? b.neoMinPerSite ?? defaults.neoMinPerSite,
    neoStaffRate: effectiveNeo.staffRate ?? custom.neoStaffRate ?? b.neoStaffRate ?? defaults.neoStaffRate,
    mcStandalonePerSite: effectiveMc.standalonePerSite ?? custom.mcStandalonePerSite ?? b.mcStandalonePerSite ?? defaults.mcStandalonePerSite,
    mcWithNeoPerSite: effectiveMc.withNeoPerSite ?? effectiveMc.neoDiscountPerSite ?? custom.mcWithNeoPerSite ?? b.mcWithNeoPerSite ?? defaults.mcWithNeoPerSite,
    linkBasePerSite: link.basePerSite ?? custom.linkBasePerSite ?? b.linkBasePerSite ?? defaults.linkBasePerSite,
    linkIncludedOrders: link.includedOrders ?? custom.linkIncludedOrders ?? b.linkIncludedOrders ?? defaults.linkIncludedOrders,
    linkOveragePerOrder: link.overagePerOrder ?? custom.linkOveragePerOrder ?? b.linkOveragePerOrder ?? defaults.linkOveragePerOrder,
    bundlePrice: bundle.price ?? bundle.monthlyPrice ?? custom.bundlePrice ?? b.bundlePrice ?? defaults.bundlePrice,
  } : defaults;
  const enterprise = b.enterpriseBundleEnabled === true || b.enterprise?.enabled === true || b.billingRules?.enterprise?.enabled === true ||
    (b.billingMode || b.plan || b.activationMode) === "enterprise_bundle";
  return {
    ...Object.fromEntries(rateKeys.map((key) => [key, String(rates[key])])),
    useOverrides: customEnabled(b),
    neoStatus: (b.invoiceSummary?.baseStatuses?.neo || b.neoStatus || b.status || "trial") === "trial" ? "trial" : "active",
    mcStatus: (b.invoiceSummary?.baseStatuses?.mc || b.mcStatus || b.status || "trial") === "trial" ? "trial" : "active",
    billingTestMode: b.billingTestMode === true,
    graceDays: String(b.graceDays ?? 3),
    liveStores: String(b.liveStores ?? preview?.neo?.rows?.length ?? 0),
    mcSites: String(b.mcSites ?? preview?.mc?.rows?.filter((row) => row.active).length ?? 0),
    plan: b.plan || "neo_usage", activationMode: b.activationMode || "usage_based",
    bundleEnabled: customEnabled(b) ? (bundle.enabled ?? custom.bundleEnabled ?? defaults.bundleEnabled) !== false : defaults.bundleEnabled !== false,
    billingMode: b.billingMode || (customEnabled(b) ? custom.strategy?.billingMode || custom.billingMode : "") || defaults.billingMode,
    enterpriseBundleEnabled: enterprise,
    enterpriseFlatFee: String(b.enterpriseFlatFee ?? b.enterprise?.flatFee ?? b.billingRules?.enterprise?.flatFee ?? defaults.enterpriseFlatFee),
    reason: "",
  };
}

function number(value, label, { integer = false, max = 10000000 } = {}) {
  const n = Number(value);
  if (String(value).trim() === "" || !Number.isFinite(n) || n < 0 || n > max || (integer && !Number.isInteger(n))) {
    throw new Error(`${label}: enter ${integer ? "a whole number" : "an amount"} from 0 to ${max}.`);
  }
  if (!integer && Math.abs(n * 100 - Math.round(n * 100)) > 0.000001) throw new Error(`${label}: use at most two decimal places.`);
  return n;
}
export function pricingSettings(form, defaults = PRICING_DEFAULTS) {
  if (form.reason.trim().length < 5 || form.reason.trim().length > 500) throw new Error("Add a reason of 5 to 500 characters for this change.");
  const enterprise = form.enterpriseBundleEnabled;
  const settings = {
    neoStatus: form.neoStatus, mcStatus: form.mcStatus, billingTestMode: form.billingTestMode,
    graceDays: number(form.graceDays, "Agreed grace days", { integer: true, max: 30 }),
    liveStores: number(form.liveStores, "Configured Neo stores", { integer: true }),
    mcSites: number(form.mcSites, "Configured MC sites", { integer: true }),
    plan: !enterprise && form.plan === "enterprise_bundle" ? "neo_mc" : form.plan,
    activationMode: !enterprise && form.activationMode === "enterprise_bundle" ? "usage_based" : form.activationMode,
    billingRulesOverrideEnabled: form.useOverrides,
    enterpriseBundleEnabled: enterprise,
    enterpriseFlatFee: number(form.enterpriseFlatFee, "Enterprise monthly fee"),
    billingMode: enterprise ? "enterprise_bundle" : form.useOverrides && form.billingMode !== "enterprise_bundle" ? form.billingMode : defaults.billingMode === "enterprise_bundle" ? "eligible_sites" : defaults.billingMode,
  };
  // Inheriting defaults keeps the stored custom prices available without activating them.
  if (form.useOverrides) {
    const labels = { neoMinPerSite: "Neo minimum / site", neoStaffRate: "Neo staff rate", mcStandalonePerSite: "MC standalone / site", mcWithNeoPerSite: "MC with Neo / site", linkBasePerSite: "Link base / site", linkIncludedOrders: "Link included orders", linkOveragePerOrder: "Link overage / order", bundlePrice: "Bundle / site" };
    const rates = Object.fromEntries(rateKeys.map((key) => [key, number(form[key], labels[key], { integer: key === "linkIncludedOrders" })]));
    Object.assign(settings, { neoMinPerSite: rates.neoMinPerSite, neoStaffRate: rates.neoStaffRate, mcStandalonePerSite: rates.mcStandalonePerSite, mcWithNeoPerSite: rates.mcWithNeoPerSite });
    settings.billingRules = {
      neo: { minimumPerSite: rates.neoMinPerSite, staffRate: rates.neoStaffRate },
      morpheus: { standalonePerSite: rates.mcStandalonePerSite, withNeoPerSite: rates.mcWithNeoPerSite },
      link: { basePerSite: rates.linkBasePerSite, includedOrders: rates.linkIncludedOrders, overagePerOrder: rates.linkOveragePerOrder },
      bundle: { enabled: form.bundleEnabled, price: rates.bundlePrice },
      enterprise: { enabled: enterprise, flatFee: settings.enterpriseFlatFee },
      strategy: { billingMode: settings.billingMode },
    };
  }
  return settings;
}
